import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spin, Alert } from 'antd';
import CurrencyInput from 'react-currency-input-field';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import { useCurrentUser } from '../../../../contexts/UserContext';
import type { selectedQuote } from './FinancingGridCard';
import './oneButton-css/RoofQuoteDetails.css';
import './oneButton-css/BatteryQuoteDetails.css';
import { GenerateQuoteSectionHead } from './GenerateQuoteSectionHead';
import type { GqSectionStat } from './GenerateQuoteSectionHead.types';
import { OBDSelect } from './form-elements';

interface BatteryQuoteDetailsProps {
    confirmQuoteCellKey: selectedQuote | null;
    priceQuote: any;
    theme: string;
    lightreachPriceCapsData: any[];
    updateLoaderCount: (loader: boolean) => void;
    updateErrorMessage: (message: string) => void;
    updateBatteryDetails: (batteryDetails: any) => void;
    solarMasterSync?: {
        enabled: boolean;
        termApr?: string;
        pricingMethod?: string;
        escalator?: string;
    };
}

export const BatteryQuoteDetails: React.FC<BatteryQuoteDetailsProps> = ({
    confirmQuoteCellKey,
    priceQuote,
    theme,
    lightreachPriceCapsData,
    updateLoaderCount,
    updateErrorMessage,
    updateBatteryDetails,
    solarMasterSync,
}) => {
    const {
        systemDesign,
        opportunityDetails,
        financePartnersList,
        productType: globalProductType,
        // updateBatteryDetails
    } = useOneButtonStore();

    const { hasCapability } = useCurrentUser();

    const [enabledFields, setEnabledFields] = useState<string[]>([]);

    // --- Form State ---
    const [financePartnerId, setFinancePartnerId] = useState<string | null>(null);
    const [financeMethodId, setFinanceMethodId] = useState<string | null>(null);
    const [pricingMethod, setPricingMethod] = useState<string | null>(null);
    const [escalator, setEscalator] = useState<string | null>(null);
    const [price, setPrice] = useState<number>(0);
    const [termApr, setTermApr] = useState<string | null>(null);
    const [rebate, setRebate] = useState<number>(0);
    const [downPayment, setDownPayment] = useState<number>(0);
    const [depositAmount, setDepositAmount] = useState<number>(0);
    const [tax, setTax] = useState<number>(0);
    const [dealerFee, setDealerFee] = useState<number>(0);
    const [offerId, setOfferId] = useState<string | null>(null);
    const [uponCompletionOfPhysicalInstall, setUponCompletionOfPhysicalInstall] = useState<number>(0);
    const [uponCompletionOfWork, setUponCompletionOfWork] = useState<number>(0);

    // --- Battery Selection State ---
    const [priceTier, setPriceTier] = useState<string>('Standard');
    const [selectedAdders, setSelectedAdders] = useState<string[]>([]);
    const [additionalCost, setAdditionalCost] = useState<number>(0);

    // --- Dynamic Options ---
    const [financeMethods, setFinanceMethods] = useState<{ value: string, label: string }[]>([]);
    const [termAprOptions, setTermAprOptions] = useState<{ value: string, label: string }[]>([]);
    const [pricingMethods, setPricingMethods] = useState<{ value: string, label: string }[]>([]);
    const [escalatorOptions, setEscalatorOptions] = useState<{ value: string, label: string }[]>([]);

    // --- Calculated Data ---
    const [detailValue, setDetailValue] = useState<any>({
        grossSalesPrice: 0,
        Financed_Amount: 0,
        totalSalesCommission: 0,
        totalAdderCost: 0,
        batteryCostSubTotal: 0,
        floorTierPrice: 0,
        minTierPrice: 0,
        midTierPrice: 0,
        stdTierPrice: 0,
        Monthly_Finance_Partner_Payment: 0
    });

    const [showCommission, setShowCommission] = useState(false);

    /** Latest numeric values for Excel payload without listing them in updateCalculations deps (avoids recalc on every keystroke). */
    const additionalCostRef = useRef(additionalCost);
    const depositAmountRef = useRef(depositAmount);
    additionalCostRef.current = additionalCost;
    depositAmountRef.current = depositAmount;

    /** Values last applied in a successful Battery Calculator response (for blur dedupe). */
    const lastCalcCurrencyRef = useRef<{ additionalCost: number; depositAmount: number } | null>(null);
    const calculationInFlightRef = useRef(false);

    const canViewCommission = hasCapability('Commission_Amount');
    const canToggleCommission = hasCapability('View_Commission_Amount');

    const getProductCode = useCallback(() => {
        const interest = systemDesign?.OpportunityInterest || opportunityDetails?.OpportunityInterest;
        let product = (interest?.BatteryOnly) ? 'B' : (globalProductType === 'BR' ? 'B' : globalProductType || 'B');

        const isMulti = interest?.Solar && interest?.Battery && (interest?.Roof || interest?.RRR);
        if (isMulti && (financeMethodId?.includes('Storage') || financeMethodId?.includes('Lease'))) {
            product = 'SB';
        }
        product = (confirmQuoteCellKey?.partner == "Good Leap") && product === 'BR' ? 'B' : product;
        if (confirmQuoteCellKey?.partner == "Good Leap"  && (product === 'SB' || product === 'SBR')) {
            if ( financeMethodId == 'Battery Loan') {
                product = 'B';
            } else if (financeMethodId?.includes('Storage')) {
                product = 'SB';
            }
        }

        return product;
    }, [systemDesign, opportunityDetails, globalProductType, financeMethodId]);

    // Initial Hydration from systemDesign or confirmQuoteCellKey
    useEffect(() => {

        if (!confirmQuoteCellKey) return;

        const b = systemDesign?.Battery || {};
        const partnerName = confirmQuoteCellKey.partner;
        const matchedPartner = financePartnersList.find(p => p.Text.toLowerCase().replace(' ', '') === partnerName?.toLowerCase().replace(' ', ''));

        if (matchedPartner) {
            setFinancePartnerId(String(matchedPartner.Value));
            handlePartnerChange(String(matchedPartner.Value), true);
        }

        // const gridMethod = confirmQuoteCellKey.methord;
        // if (gridMethod) setFinanceMethodId(gridMethod);

        // Pre-fill other fields from systemDesign if available
        setPricingMethod(confirmQuoteCellKey.methord || null);
        setPrice(Number(confirmQuoteCellKey.price || 0));
        setEscalator(String(confirmQuoteCellKey.ExcelOffer?.escalator) || null);
        let tier = 'Standard';
        switch(confirmQuoteCellKey.ExcelOffer?.offerType?.toLocaleLowerCase()){
            case 'floorbattery':
                tier = 'Floor';
                break;
            case 'minbattery':
                tier = 'Min';
                break;
            case 'maxbattery':
                tier = 'Max';
                break;
            default:
                tier = 'Standard';
        }
        setPriceTier(tier || 'Standard');

        // con
        // let termAndApr = ''
        // setTermApr( || null);
        // setRebate(Number(b.Rebate || 0));
        // setDownPayment(Number(b.DownPayment || 0));
        // setDepositAmount(Number(b.Deposit_Amount || 0));
        // setTax(Number(b.Tax || 0));
        // setDealerFee(Number(b.DealerFee || 0));
        // setPriceTier(systemDesign?.BatteryOnly?.PriceTier || 'Standard');
        // setSelectedAdders(systemDesign?.BatteryOnly?.BatteryAdder || []);
        // setAdditionalCost(Number(systemDesign?.BatteryOnly?.AdditionalCost || 0));

    }, [confirmQuoteCellKey]); // eslint-disable-line

    const handlePartnerChange = async (partnerId: string, resetFields: boolean = true) => {
        const partner = financePartnersList.find(p => String(p.Value) === partnerId);
        if (!partner) return;

        updateLoaderCount(true);
        try {
            const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
            const product = getProductCode();

            if (partner.Text === 'Sunnova') {
                const payload = {
                    ExcelFileName: "Battery Calculator",
                    SheetName: "Battery IO",
                    InputValues: [
                        { Type: "String", CellNumber: "C3", CellValue: stateId },
                        { Type: "String", CellNumber: "C4", CellValue: product },
                        { Type: "String", CellNumber: "C7", CellValue: partner.Text }
                    ],
                    OutputCells: ["F14", "F15"]
                };
                const res = await masterServices.cascadingDropDown(payload);
                if (res) {
                    const methods = String(res[0]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                    const terms = String(res[1]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                    setFinanceMethods(methods.reverse());
                    setTermAprOptions(terms);
                    if (resetFields && methods.length > 0) setFinanceMethodId(methods[0].value);
                    if (resetFields && terms.length > 0) {
                        setTermApr((prev) => (prev && terms.some((t) => t.value === prev) ? prev : terms[0].value));
                    }
                }
            } else {
                if (!confirmQuoteCellKey?.methord || confirmQuoteCellKey?.methord == 'CASH') {
                    const payload = {
                        ExcelFileName: "Sunnova Financial Data",
                        SheetName: "SVA Financing Options",
                        InputValues: [
                            { Type: "String", CellNumber: "B4", CellValue: stateId },
                            { Type: "String", CellNumber: "C4", CellValue: product },
                            { Type: "String", CellNumber: "D4", CellValue: partner.Text },
                            { Type: "String", CellNumber: "E8", CellValue: systemDesign?.SolarUtility?.UtilityCompanyName || '' }
                        ],
                        OutputCells: ["E4"]
                    };
                    const res = await masterServices.cascadingDropDown(payload);
                    if (res) {
                        const methods = String(res[0]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                        setFinanceMethods(methods);
                        if (resetFields && methods.length > 0) setFinanceMethodId(methods[0].value);
                    }
                } else {
                    setFinanceMethods([{ value: confirmQuoteCellKey?.methord, label: confirmQuoteCellKey?.methord }]);
                    setFinanceMethodId(confirmQuoteCellKey?.methord);
                }

            }
        } catch (err) {
        } finally {
            updateLoaderCount(false);
        }
    };

    const getFormValue = (ele: string): string => {
        switch (ele) {
            case 'Term & APR':
                return termApr ?? '';
            case 'Deposit Amount':
            case 'Deposit':
                return String(depositAmount);
            case 'Rebate':
                return String(rebate);
            case 'Down Payment':
                return String(downPayment);
            case 'Tax':
                return String(tax);
            case 'Dealer Fee':
                return String(dealerFee);
            case 'Upon Completion of Physical Install':
                return String(uponCompletionOfPhysicalInstall);
            case 'Upon Completion of Work':
                return String(uponCompletionOfWork);
            default:
                return '';
        }
    };

    const setAmount = (ele: string, value: number) => {
        switch (ele) {
            case 'Deposit Amount':
            case 'Deposit':
                setDepositAmount(value);
                break;
            case 'Rebate':
                setRebate(value);
                break;
            case 'Down Payment':
                setDownPayment(value);
                break;
            case 'Tax':
                setTax(value);
                break;
            case 'Dealer Fee':
                setDealerFee(value);
                break;
            case 'Upon Completion of Physical Install':
                setUponCompletionOfPhysicalInstall(value);
                break;
            case 'Upon Completion of Work':
                setUponCompletionOfWork(value);
                break;
            default:
                break;
        }
    };



    useEffect(() => {
        if (!financePartnerId || !financeMethodId) return;

        const fetchMethodDetails = async () => {
            const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
            if (!partner) return;

            updateLoaderCount(true);
            try {
                const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
                const product = getProductCode();
                const payload = {
                    ExcelFileName: "Sunnova Financial Data",
                    SheetName: "SVA Financing Options",
                    InputValues: [
                        { Type: "String", CellNumber: "B4", CellValue: stateId },
                        { Type: "String", CellNumber: "C4", CellValue: product },
                        { Type: "String", CellNumber: "D4", CellValue: partner.Text },
                        { Type: "String", CellNumber: "F4", CellValue: financeMethodId },
                        { Type: "String", CellNumber: "E8", CellValue: systemDesign?.SolarUtility?.UtilityCompanyName || '' }
                    ],
                    OutputCells: ["G4", "N4", "H4", "L4", "I4"]
                };

                const res = await masterServices.cascadingDropDown(payload);
                if (res) {

                    let enabledFields = res[0].CellValue.split('|').map((v: any) => v.trim());
                    if(partner.Text == "Cash"){
                        enabledFields.push("Deposit");
                        enabledFields.push("Upon Completion of Physical Install");
                        enabledFields.push("Upon Completion of Work");
                    }

                    if (partner.Text.toLowerCase() != "goodleap") {
                        enabledFields.push("Deposit");
                    }

                    setEnabledFields(enabledFields);
                    const pNames = String(res[2]?.CellValue || '').split('|');
                    const pValues = String(res[4]?.CellValue || '').split('|');
                    setPricingMethods(pNames.map((n, i) => ({ value: pValues[i]?.trim() || n.trim(), label: n.trim() })));

                    const terms = String(res[1]?.CellValue || '')
                        .split('|')
                        .filter(Boolean)
                        .map((v) => ({ value: v.trim(), label: v.trim() }));

                    if (terms.length > 0) {
                        setTermAprOptions(terms);

                        if (partner.Text === 'Good Leap' || partner.Text === 'Standard') {
                            const cellTerm = Number(confirmQuoteCellKey?.term);
                            const cellApr = Number(confirmQuoteCellKey?.apr);
                            let selectedMatchedValue = '';

                            if (!Number.isNaN(cellTerm) && !Number.isNaN(cellApr)) {
                                const target = `${cellTerm} years at ${cellApr}%`.toLowerCase();
                                const found = terms.find((t) => {
                                    const v = t.value.toLowerCase();
                                    if (v === target) return true;
                                    const nums = v.match(/\d+(\.\d+)?/g)?.map(Number);
                                    return nums?.includes(cellTerm) && nums?.includes(cellApr);
                                });
                                if (found) selectedMatchedValue = found.value;
                            }

                            if (selectedMatchedValue) {
                                setTermApr(selectedMatchedValue);
                            } else {
                                setTermApr((prev) => {
                                    if (prev && terms.some((t) => t.value === prev)) return prev;
                                    return terms[0].value;
                                });
                            }
                        } else {
                            setTermApr((prev) => {
                                if (prev && terms.some((t) => t.value === prev)) return prev;
                                return terms[0].value;
                            });
                        }
                    }

                    const escs = String(res[3]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                    setEscalatorOptions(escs);
                }
            } catch (err) {
                console.error("Method Change Error:", err);
            } finally {
                updateLoaderCount(false);
            }
        };

        fetchMethodDetails();
    }, [financeMethodId, financePartnerId]); // eslint-disable-line

    const updateCalculations = useCallback(async () => {
        if (!financePartnerId || !financeMethodId) return;
        if (calculationInFlightRef.current) return;
        calculationInFlightRef.current = true;
        const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
        const additionalCostSnapshot = additionalCostRef.current;
        const depositAmountSnapshot = depositAmountRef.current;
        updateLoaderCount(true);
        try {

            const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
            const product = getProductCode();
            const bi = systemDesign?.BatteryInformation || {};

            const payload = {
                ExcelFileName: "Battery Calculator",
                SheetName: "Battery IO",
                InputValues: [
                    { Type: "String", CellNumber: "C3", CellValue: stateId },
                    { Type: "String", CellNumber: "C4", CellValue: product },
                    { Type: "String", CellNumber: "C5", CellValue: bi.Manufacturer || '' },
                    { Type: "String", CellNumber: "C6", CellValue: String(bi.Quantity || '0') },
                    { Type: "String", CellNumber: "C7", CellValue: partner?.Text || '' },
                    { Type: "String", CellNumber: "C8", CellValue: financeMethodId },
                    { Type: "String", CellNumber: "C9", CellValue: termApr || '' },
                    { Type: "String", CellNumber: "C10", CellValue: priceTier },
                    { Type: "String", CellNumber: "C11", CellValue: selectedAdders.includes('Battery Fire Enclosure') ? 'Yes' : '' },
                    { Type: "String", CellNumber: "C12", CellValue: selectedAdders.includes('Lumin Smart Panel') ? 'Yes' : '' },
                    { Type: "String", CellNumber: "C13", CellValue: String(additionalCostSnapshot) },
                    { Type: "String", CellNumber: "C14", CellValue: String(depositAmountSnapshot) }
                ],
                OutputCells: ["F5", "F7", "F8", "F9", "F10", "F11", "F12", "F13", "F6", "AF4", "AF3", "F16"]
            };

            const res = await masterServices.cascadingDropDown(payload);
            if (res) {
                const getVal = (cell: string) => res.find((o: any) => o.CellNumber === cell)?.CellValue || 0;
                setDetailValue({
                    grossSalesPrice: Number(getVal('F5')),
                    totalSalesCommission: Number(getVal('F7')),
                    floorTierPrice: Number(getVal('F8')),
                    minTierPrice: Number(getVal('F9')),
                    midTierPrice: Number(getVal('F10')),
                    stdTierPrice: Number(getVal('F11')),
                    batteryCostSubTotal: Number(getVal('F12')),
                    totalAdderCost: Number(getVal('F13')),
                    Financed_Amount: Number(getVal('F16')),
                    Monthly_Finance_Partner_Payment: Number(getVal('F6'))
                });

                if(Number(depositAmountSnapshot) > Number(getVal('F5'))){
                    updateErrorMessage(`The entered Deposit Amount cannot be greater than ${Number(getVal('F5')).toFixed(2)}`);
                }

                lastCalcCurrencyRef.current = {
                    additionalCost: additionalCostSnapshot,
                    depositAmount: depositAmountSnapshot,
                };

                if (partner?.Text == 'Good Leap') {
                    setPrice(detailValue.batteryCostSubTotal);
                }

                // if(this.batteryOnlyDetails.BatteryAdder.length>0) {
                //     let batteryIndex = this.batteryOnlyDetails.BatteryAdder.findIndex((el:any) => el == 'Battery Fire Enclosure');
                //     let luminIndex = this.batteryOnlyDetails.BatteryAdder.findIndex((el:any) => el == 'Lumin Smart Panel');
                //     if(batteryIndex>-1) this.updateAdder('Battery Fire Enclosure', this.detailValue.totalAdderCost, this.detailValue.totalAdderCost);
                //     else this.removeArray('Battery Fire Enclosure')
                //     if(luminIndex>-1) this.updateAdder('Lumin Smart Panel' , this.detailValue.totalAdderCost, this.detailValue.totalAdderCost);
                //     else this.removeArray('Lumin Smart Panel')
                // } else {
                //     this.BatteryAddersCost = []
                //     let defaultIndex = this.BatteryAddersCost.findIndex((el:any) => el.adderSelect == '');
                //     if(defaultIndex>-1){
                //         this.BatteryAddersCost[defaultIndex].amount =  this.batteryOnlyDetails.AdditionalCost;
                //         this.BatteryAddersCost[defaultIndex].pricing_Type =  this.batteryOnlyDetails.PriceTier;
                //     }else{
                //         this.BatteryAddersCost.push(
                //         {
                //         adderSelect: '',
                //         Adder_Cost: 0,
                //         amount: this.batteryOnlyDetails.AdditionalCost,
                //         pricing_Type: this.batteryOnlyDetails.PriceTier
                //         });
                //     }
                // }
            }
        } catch (err) {
            console.error("Calculation Error:", err);
        } finally {
            calculationInFlightRef.current = false;
            updateLoaderCount(false);
        }
        if (partner?.Text == 'Good Leap') goodLeapDropDownChange();
    }, [financePartnerId, financeMethodId, termApr, priceTier, selectedAdders]); // eslint-disable-line

    /** Runs Battery Calculator when additional cost / deposit amounts change; skips if values match last successful run. */
    const commitBatteryCalculatorIfValuesChanged = useCallback(() => {
        const ac = additionalCostRef.current;
        const dep = depositAmountRef.current;
        const last = lastCalcCurrencyRef.current;
        if (last !== null && ac === last.additionalCost && dep === last.depositAmount) {
            return;
        }
        void updateCalculations();
    }, [updateCalculations]);

    const parseCurrencyInput = (value: string | undefined) => {
        if (value === undefined || value === '') return 0;
        const n = Number(String(value).replace(/,/g, ''));
        return Number.isFinite(n) ? n : 0;
    };

    const handleCurrencyAmountChange = (ele: string, value: string | undefined) => {
        setAmount(ele, parseCurrencyInput(value));
    };

    useEffect(() => {
        updateCalculations();
    }, [updateCalculations]);

    useEffect(() => {
        if (!solarMasterSync?.enabled) return;

        const syncedTermApr = String(solarMasterSync.termApr ?? '').trim();
        if (syncedTermApr && termAprOptions.some((o) => o.value === syncedTermApr) && termApr !== syncedTermApr) {
            setTermApr(syncedTermApr);
        }

        const syncedPricingMethod = String(solarMasterSync.pricingMethod ?? '').trim();
        if (syncedPricingMethod && pricingMethods.length > 0) {
            const matchedPricingMethod = pricingMethods.find(
                (o) => String(o.value).trim() === syncedPricingMethod || String(o.label).trim() === syncedPricingMethod,
            );
            if (matchedPricingMethod && pricingMethod !== matchedPricingMethod.value) {
                setPricingMethod(matchedPricingMethod.value);
            }
        }

        const syncedEscalator = String(solarMasterSync.escalator ?? '').trim();
        if (syncedEscalator && escalatorOptions.length > 0) {
            const matchedEscalator = escalatorOptions.find(
                (o) => String(o.value).trim() === syncedEscalator || String(o.label).trim() === syncedEscalator,
            );
            if (matchedEscalator && escalator !== matchedEscalator.value) {
                setEscalator(matchedEscalator.value);
                if(financePartnerId){
                    const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
                    if (partner?.Text == 'Good Leap') goodLeapDropDownChange();
                }
            }
        }
    }, [
        solarMasterSync,
        termAprOptions,
        pricingMethods,
        escalatorOptions,
        termApr,
        pricingMethod,
        escalator,
    ]);

    // Sync to store for GenerateQuoteModal
    useEffect(() => {
        const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
        const [term, apr] = (termApr) ? termApr.replace("APR", "").split(" years at ") : ["0", "0"];
        updateBatteryDetails({
            financePartnerId,
            financePartnerName: partner?.Text || '',
            financeMethodName: financeMethodId,
            pricingMethod,
            price: price,
            termAndApr: termApr,
            term: Number(String(term).trim()),
            apr: Number(String(apr).replace("%", "").trim()),
            rebate,
            downPayment,
            depositAmount,
            tax,
            dealerFee,
            totalCommissionableAmount: detailValue.totalSalesCommission,
            grossSalesPrice: detailValue.grossSalesPrice,
            financedAmount: detailValue.Financed_Amount,
            monthlyPayment: detailValue.Monthly_Finance_Partner_Payment,
            priceTier: priceTier,
            selectedAdders,
            additionalCost,
            totalAdderCost: detailValue.totalAdderCost,
            valid: !!financePartnerId && !!financeMethodId && (termAprOptions.length > 0 ? !!termApr : true) && !lightreachQuoteError(),
            uponCompletionOfPhysicalInstall,
            uponCompletionOfWork,
            Offer_Id: offerId
        });
    }, [financePartnerId, financeMethodId, pricingMethod, price, termApr, rebate, downPayment, depositAmount, tax, dealerFee, detailValue, priceTier, selectedAdders, additionalCost, financePartnersList, offerId]); // eslint-disable-line

    const goodLeapDropDownChange = async () => {
        try {

            const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
            const product = getProductCode();
            const bi = systemDesign?.BatteryInformation || {};
            const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
            updateLoaderCount(true);
            const payload = {
                ExcelFileName: "Sunnova Financial Data",
                SheetName: "SVA Financing Options",
                InputValues: [

                    {
                        "Type": "String",
                        "CellNumber": "B4",
                        "CellValue": stateId
                    },
                    {
                        "Type": "String",
                        "CellNumber": "C4",
                        "CellValue": product
                    },
                    {
                        "Type": "String",
                        "CellNumber": "D4",
                        "CellValue": partner?.Text || ''
                    },
                    {
                        "Type": "String",
                        "CellNumber": "F4",
                        "CellValue": financeMethodId
                    },
                    {
                        "Type": "String",
                        "CellNumber": "K4",
                        "CellValue": 0
                    },
                    {
                        "Type": "String",
                        "CellNumber": "M4",
                        "CellValue": ''
                    },
                    {
                        "Type": "String",
                        "CellNumber": "O4",
                        "CellValue": termApr || ''
                    },
                    {
                        "Type": "String",
                        "CellNumber": "E8",
                        "CellValue": systemDesign?.SolarUtility?.UtilityCompanyName || ''
                    }
                ],
                OutputCells: ["T4", "U11"]
            };

            const res = await masterServices.cascadingDropDown(payload);
            if (res) {
                updateLoaderCount(false);
                setOfferId(res[0].CellValue);
            }
        } catch (err) {
            console.error("Calculation Error:", err);
        } finally {
            updateLoaderCount(false);
        }
    }


    const toggleAdder = (adder: string) => {
        setSelectedAdders(prev => prev.includes(adder) ? prev.filter(a => a !== adder) : [...prev, adder]);
    };

    const formatCurrency = (val: any) => {
        const num = Number(val || 0);
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };

    const lightreachQuoteError = () => {
        if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'lightreach' && lightreachPriceCapsData?.length === 0) {
            return true;
        }
        if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'lightreach' && validateLightreachQuote()) {
            return true;
        }
        return false;
    }

    const validateLightreachQuote = () => {
        if (!priceQuote) return true;
        const batterycount = priceQuote.BatteryInformation?.Quantity;
        const modelName = priceQuote.BatteryInformation?.FinancePartnerModelName;
        const manufacturer = priceQuote.BatteryInformation?.Manufacturer;
        const totalSalesAmount = detailValue.grossSalesPrice;
        if (!batterycount || !modelName || !manufacturer || !totalSalesAmount) {
          return true;
        }
        const matchedProduct = lightreachPriceCapsData.find((price: any) =>
          price.model === modelName && price.manufacturer === manufacturer
        );
        if (matchedProduct) {
          const calculatedAmount = matchedProduct.maxPrice * batterycount;
          if (totalSalesAmount > calculatedAmount) {
            return true;
          }
          return false;
        }
        return  true;
      }

    if (!confirmQuoteCellKey) return null;

    const batteryHeaderStats: GqSectionStat[] = [
        { label: 'Total sales amount', value: `$${formatCurrency(detailValue.grossSalesPrice)}` },
        { label: 'Financed amount', value: `$${formatCurrency(detailValue.Financed_Amount)}` },
        {
            label: 'Commission amount',
            value: `$${formatCurrency(detailValue.totalSalesCommission)}`,
            emphasize: true,
        },
    ];

    return (
        <div className="obd-roof-quote-details__container obd-roof-quote-details__container--split obd-gq-section">
            <GenerateQuoteSectionHead
                title="Battery"
                stats={batteryHeaderStats}
                ariaLabel="Battery quote totals"
            />
            <div className="obd-gq-section-body">
            <div className="obd-generate-quote-split obd-generate-quote-split--no-aside">
                <div className="obd-generate-quote-split__forms">
                    <div className="obd-gq-product-line">
                        {confirmQuoteCellKey?.partner} — Battery 
                    </div>
            {/* Main Configuration Grid */}
            <div className="obd-roof-quote-details__top-grid">
                <div className="obd-roof-quote-details__field-group">
                    <label className="obd-roof-quote-details__label">Finance Method</label>
                    <OBDSelect
                        className="obd-roof-quote-details__select"
                        value={financeMethodId}
                        onChange={setFinanceMethodId}
                        options={financeMethods}
                        disabled
                        popupClassName="obd-select-dropdown"
                    />
                </div>
                {
                    ['Term & APR', 'Deposit Amount', 'Rebate', 'Escalator', 'Down Payment', 'Tax', 'Dealer Fee', 'Deposit', "Upon Completion of Physical Install","Upon Completion of Work"].map(
                        (ele) => {
                            if (enabledFields.includes(ele)) {
                                return (
                                    <div key={ele} className="obd-roof-quote-details__field-group">
                                        <label className="obd-roof-quote-details__label">{ele === 'Deposit'?'Deposit Amount':ele}</label>
                                        {ele == "Term & APR" ? (
                                            <OBDSelect
                                                className="obd-roof-quote-details__select"
                                                value={termApr ?? undefined}
                                                onChange={(v) => setTermApr(v)}
                                                options={termAprOptions}
                                                placeholder={termAprOptions.length > 0 ? 'Select term' : 'No terms available'}
                                                disabled={termAprOptions.length === 0 || Boolean(solarMasterSync?.enabled)}
                                                popupClassName="obd-select-dropdown"
                                                getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                                            />
                                        ) : (
                                            ele === 'Pricing Method' ? (
                                                <OBDSelect
                                                    className="obd-roof-quote-details__select"
                                                    value={pricingMethod ?? undefined}
                                                    onChange={(v) => setPricingMethod(v)}
                                                    options={pricingMethods}
                                                    disabled={Boolean(solarMasterSync?.enabled)}
                                                />
                                            ) : (

                                            ele === 'Escalator' ? (
                                                <OBDSelect
                                                    className="obd-roof-quote-details__select"
                                                    value={escalator ?? undefined}
                                                    onChange={(v) => setEscalator(v)}
                                                    options={escalatorOptions}
                                                    disabled={Boolean(solarMasterSync?.enabled)}
                                                />
                                            ) : (

                                                <CurrencyInput
                                                    className="obd-roof-quote-details__input"
                                                    value={Number(getFormValue(ele)).toFixed(2)}
                                                    onValueChange={(v) => handleCurrencyAmountChange(ele, v)}
                                                    onBlur={
                                                        ele === 'Deposit' || ele === 'Deposit Amount'
                                                            ? commitBatteryCalculatorIfValuesChanged
                                                            : undefined
                                                    }
                                                    prefix="$"
                                                    decimalsLimit={2}
                                                    fixedDecimalLength={2}
                                                    allowNegativeValue={false}
                                                />
                                        )))}
                                    </div>
                                )
                            }
                        }
                    )
                }

            </div>

            {/* Tier Selection */}
            <div className="obd-battery-quote-details__tier-section">
                <h3 className="obd-battery-quote-details__section-title">Choose battery price</h3>
                <div
                    className="obd-battery-quote-details__battery-info"
                    aria-label="Battery system information"
                >
                    <div className={`obd-battery-quote-details__field-group ${!systemDesign?.BatteryInformation?.Manufacturer ? 'error' : ''}`}>
                        <label className="obd-battery-quote-details__label">Battery Manufacturer</label>
                        <div className="obd-battery-quote-details__read-only">
                            {systemDesign?.BatteryInformation?.Manufacturer || 'Not selected'}
                        </div>
                    </div>
                    <div className={`obd-battery-quote-details__field-group ${!systemDesign?.BatteryInformation?.Quantity ? 'error' : ''}`}>
                        <label className="obd-battery-quote-details__label">Battery Count</label>
                        <div className="obd-battery-quote-details__read-only">
                            {systemDesign?.BatteryInformation?.Quantity || 0}
                        </div>
                    </div>
                </div>
                <div className="obd-battery-quote-details__section-header">
                    <h3 className="obd-battery-quote-details__section-header-title">Select Pricing Tier</h3>
                    <div
                        className="obd-battery-quote-details__section-header-subtotal"
                        aria-label={`Battery cost subtotal ${formatCurrency(detailValue.batteryCostSubTotal)}`}
                    >
                        <span className="obd-battery-quote-details__subtotal-label">Battery Cost Subtotal</span>
                        <span className="obd-battery-quote-details__subtotal-value">
                            ${formatCurrency(detailValue.batteryCostSubTotal)}
                        </span>
                    </div>
                </div>
                <div className="obd-battery-quote-details__tier-grid">
                    {[
                        { label: 'Floor', value: 'Floor', price: detailValue.floorTierPrice },
                        { label: 'Min', value: 'Min', price: detailValue.minTierPrice },
                        { label: 'Mid', value: 'Mid', price: detailValue.midTierPrice },
                        { label: 'Standard', value: 'Standard', price: detailValue.stdTierPrice },
                    ].map(tier => (
                        <div
                            key={tier.value}
                            className={`obd-battery-quote-details__tier-card ${priceTier === tier.value ? 'obd-battery-quote-details__tier-card--selected' : ''}`}
                            onClick={() => setPriceTier(tier.value)}
                        >
                            <span className="obd-battery-quote-details__tier-header">{tier.label}</span>
                            <span className="obd-battery-quote-details__tier-value">${formatCurrency(tier.price)}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Adders Selection */}
            <div className="obd-battery-quote-details__adders-section">
                <div className="obd-battery-quote-details__section-header">
                    <h3 className="obd-battery-quote-details__section-header-title">
                        Battery Adders &amp; Additional Costs
                    </h3>
                    <div
                        className="obd-battery-quote-details__section-header-subtotal"
                        aria-label={`Adder cost subtotal ${formatCurrency(detailValue.totalAdderCost)}`}
                    >
                        <span className="obd-battery-quote-details__subtotal-label">Adder Cost Subtotal</span>
                        <span className="obd-battery-quote-details__subtotal-value">
                            ${formatCurrency(detailValue.totalAdderCost)}
                        </span>
                    </div>
                </div>
                <div className="obd-battery-quote-details__adders-grid">
                    <div className="obd-battery-quote-details__adders-list">
                        <div
                            className={`obd-battery-quote-details__adder-chip ${selectedAdders.includes('Battery Fire Enclosure') ? 'obd-battery-quote-details__adder-chip--active' : ''}`}
                            onClick={() => toggleAdder('Battery Fire Enclosure')}
                        >
                            Battery Fire Enclosure
                        </div>
                        <div
                            className={`obd-battery-quote-details__adder-chip ${selectedAdders.includes('Lumin Smart Panel') ? 'obd-battery-quote-details__adder-chip--active' : ''}`}
                            onClick={() => toggleAdder('Lumin Smart Panel')}
                        >
                            Lumin Smart Panel
                        </div>
                    </div>
                    <div className="obd-battery-quote-details__field-group">
                        <label className="obd-battery-quote-details__label">Other Additional Cost</label>
                        <CurrencyInput
                            className="obd-battery-quote-details__input"
                            value={Number(additionalCost).toFixed(2)}
                            onValueChange={(v) => setAdditionalCost(parseCurrencyInput(v))}
                            onBlur={commitBatteryCalculatorIfValuesChanged}
                            prefix="$"
                            decimalsLimit={2}
                            fixedDecimalLength={2}
                            allowNegativeValue={false}
                        />
                    </div>
                </div>
            </div>

                {/* Validation Message */}
                    { confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'lightreach' && lightreachPriceCapsData?.length === 0 && (
                        <Alert message="No price cap available" type="error" showIcon />
                    )}
                    {confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'lightreach' && validateLightreachQuote() && (
                        <Alert message="The Total Sales Amount exceeds the maximum Price Cap for the selected Finance Partner. Please review the Price Caps to select a valid option." type="error" showIcon />
                    )}

                </div>
            </div>
            </div>
        </div>
    );
};

export default BatteryQuoteDetails;
